import { standardPresentation, type BlockTemplateDocument } from './template'

/**
 * The Trade Desk, translated from the admin portal's `emerald` template.
 *
 * The point of this file is the translation, not the client. `emerald` and `narrative-pilot` are
 * the only 2 of the 56 legacy templates that are designed emails rather than styled clip lists, so
 * reproducing one is the honest test of whether the new model can carry the hard cases. Everything
 * below is a value in a template document — no renderer branch says "Trade Desk" anywhere.
 *
 * What the shape needs, and where each part comes from:
 *
 * - #fafafa page, a flat 600px card → `shell`.
 * - The wordmark → the MASTHEAD, sitting clear on the card: no bar, the words in ink at 26px, not
 *   uppercased, left-aligned. That is the template editor's own control, which is the point — the
 *   header of a designed email is reachable without hand-written HTML. Swap the text for the
 *   client's mark by uploading a PNG in the editor; the text stays as the fallback because Outlook
 *   blocks externally-fetched images, and the legacy template says so in a comment.
 * - The kicker and the rule → two custom sections of hand-written HTML, in that order.
 * - No TO/CC/BCC block. The production email opens with one, above the card; Kaushik dropped it
 *   (2026-09-11) rather than have it sit under the wordmark, which is the only place a block can
 *   go. Placing anything above the card remains the shell-with-slots proposal.
 * - No salutation, no body, no signature, no insights → those blocks are simply absent. `emerald`
 *   starts at the first section bar by design.
 * - The article row and the social card → `articleHtml`, per source. A blue uppercase source label,
 *   a Ventura headline, a sentiment chip and a Read More button are not reachable by colour and
 *   size controls, which is what the escape hatch is for.
 *
 * Ventura is The Trade Desk's licensed face; it is named first and falls back to Arial, exactly as
 * the legacy template does.
 */

const BLUE = '#0372e2'
const INK = '#0c0c27'
const SLATE = '#121462'
const HAIRLINE = '#efefef'
const SANS = 'Arial,Helvetica,sans-serif'
const DISPLAY = "'Ventura',Arial,Helvetica,sans-serif"

/** "PRESS CLIPS" over the send date, which is the report's whole title treatment. */
const KICKER = `<div style="font-family:${SANS};font-size:12px;font-weight:bold;letter-spacing:0.12em;text-transform:uppercase;color:${BLUE}">Press Clips</div>
<div style="font-family:${DISPLAY};font-size:28px;line-height:1.15;font-weight:bold;color:${INK};letter-spacing:-0.01em;padding-top:4px">{{send_date}}</div>`

const RULE = `<div style="height:2px;background-color:${BLUE};font-size:0;line-height:0">&nbsp;</div>`

/**
 * The news row: source label, headline, byline, snippets, syndication, sentiment chip, button.
 *
 * The legacy byline is one composed string — "Jun 24, 2026 | By: A. Reporter" — so it is composed
 * here from the two tokens that make it. An article with no author renders "| By:" in the original;
 * this renders the separator only when there is something after it, which is the one place this
 * deliberately improves on what it is reproducing.
 */
const NEWS_ROW = `<div style="padding-top:20px">
  <div style="font-family:${SANS};font-size:11px;font-weight:bold;letter-spacing:0.08em;text-transform:uppercase;color:${BLUE}">{{outlet}}</div>
  <a href="{{url}}" style="display:block;font-family:${DISPLAY};font-size:18px;line-height:1.3;font-weight:bold;color:${INK};text-decoration:none;padding-top:3px">{{headline}}</a>
  <div style="font-family:${SANS};font-size:12px;color:${SLATE};padding-top:5px">{{date}} {{author}}</div>
  <p style="font-family:${DISPLAY};font-size:15px;line-height:1.55;color:${INK};margin:8px 0 0 0">{{snippets}}</p>
  {{#if syndicated}}<div style="font-family:${SANS};font-size:11px;font-style:italic;color:${SLATE};padding-top:10px">Syndicated By: {{syndicated}}</div>{{/if}}
  {{#if sentiment}}<table role="presentation" cellpadding="0" cellspacing="0" border="0" style="padding-top:12px"><tr>
    <td style="border:1px solid ${HAIRLINE};background-color:#f7f7f7;border-radius:4px;padding:5px 9px;font-family:${SANS};font-size:11px;color:${SLATE}"><span style="color:${BLUE};font-weight:bold;letter-spacing:0.06em;text-transform:uppercase">Sentiment</span>&nbsp;&nbsp;{{sentiment}}</td>
  </tr></table>{{/if}}
  {{#if full_text_url}}<table role="presentation" cellpadding="0" cellspacing="0" border="0" style="padding-top:12px"><tr>
    <td bgcolor="#0372E2" style="border-radius:4px"><a href="{{full_text_url}}" style="display:inline-block;padding:7px 14px;font-family:${DISPLAY};font-size:13px;color:#ffffff;text-decoration:none">Read More</a></td>
  </tr></table>{{/if}}
  <div style="height:1px;background-color:${HAIRLINE};font-size:0;line-height:0;margin-top:18px">&nbsp;</div>
</div>`

/**
 * The social card: grey panel, blue left edge, a platform badge, then the metric chips. The badge
 * is the `{{source_label}}` token — "in" for LinkedIn and "X" for X in the production email — so
 * the same name a header would put over the group is the one on the card.
 */
const SOCIAL_CARD = `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="background-color:${HAIRLINE};border-radius:4px;margin-top:18px">
  <tr><td style="padding:14px 16px;border-left:3px solid ${BLUE}">
    <span style="display:inline-block;background-color:${INK};color:#ffffff;font-family:${SANS};font-size:10px;font-weight:bold;padding:2px 6px;border-radius:3px">{{source_label}}</span>
    <span style="font-family:${SANS};font-size:13px;font-weight:bold;color:${INK}">&nbsp;{{outlet}}</span>
    <span style="font-family:${SANS};font-size:12px;color:${SLATE}">&nbsp;{{handle}} {{date}}</span>
    <p style="font-family:${DISPLAY};font-size:14px;line-height:1.5;color:${INK};margin:8px 0 0 0">{{headline}}</p>
    <table role="presentation" cellpadding="0" cellspacing="0" border="0" style="padding-top:10px"><tr>
      {{#if reach}}<td style="background-color:#ffffff;border-radius:4px;padding:5px 9px;font-family:${SANS};font-size:11px;color:${SLATE}"><span style="color:${BLUE};font-weight:bold;letter-spacing:0.06em;text-transform:uppercase">Reach</span>&nbsp;&nbsp;{{reach}}</td>
      <td style="width:8px;font-size:0">&nbsp;</td>{{/if}}
      {{#if engagement}}<td style="background-color:#ffffff;border-radius:4px;padding:5px 9px;font-family:${SANS};font-size:11px;color:${SLATE}"><span style="color:${BLUE};font-weight:bold;letter-spacing:0.06em;text-transform:uppercase">Engagement</span>&nbsp;&nbsp;{{engagement}}</td>
      <td style="width:10px;font-size:0">&nbsp;</td>{{/if}}
      {{#if sentiment}}<td style="background-color:#ffffff;border-radius:4px;padding:5px 9px;font-family:${SANS};font-size:11px;color:${SLATE}"><span style="color:${BLUE};font-weight:bold;letter-spacing:0.06em;text-transform:uppercase">Sentiment</span>&nbsp;&nbsp;{{sentiment}}</td>
      <td style="width:10px;font-size:0">&nbsp;</td>{{/if}}
      <td style="font-family:${SANS};font-size:11px"><a href="{{url}}" style="color:${BLUE};text-decoration:underline">View post</a></td>
    </tr></table>
  </td></tr>
</table>`

export function ttdTemplateDocument(): BlockTemplateDocument {
  return {
    version: 2,
    target: 'email',
    // The wordmark, as the masthead: clear of any bar, ink, 26px, as typed. Upload the client's
    // PNG in the editor to replace the words with the mark; the words remain the Outlook fallback.
    masthead: {
      text: 'The Trade Desk',
      showLogo: false,
      background: 'transparent',
      textColor: INK,
      textSizePx: 26,
      uppercase: false,
      logoAlign: 'left',
      logoLayout: 'beside',
      logoHeightPx: 40,
    },
    shell: {
      // Emerald's blue is the design, not a highlight — so the template brings it, and the
      // account's brand accent does not apply. That is what makes this one client's template.
      accent: BLUE,
      pageBackground: '#fafafa',
      cardBackground: '#ffffff',
      cardWidthPx: 600,
      framed: false,
      preheader: 'The Trade Desk — press clips.',
    },
    blocks: [
      {
        id: 'kicker',
        type: 'customSection',
        enabled: true,
        // No heading and no frame: these blocks ARE the design, and a 'plain' section
        // with an empty heading adds nothing but a 16px gap beneath.
        heading: '',
        style: 'plain',
        content: KICKER,
      },
      {
        id: 'rule',
        type: 'customSection',
        enabled: true,
        // No heading and no frame: these blocks ARE the design, and a 'plain' section
        // with an empty heading adds nothing but a 16px gap beneath.
        heading: '',
        style: 'plain',
        content: RULE,
      },
      {
        id: 'sections',
        type: 'sections',
        enabled: true,
        default: {
          ...standardPresentation(),
          // The row markup owns every field, so the built-in toggles have nothing to say. Left at
          // their defaults deliberately: a reader comparing this to another template should see
          // that the difference is the markup, not a wall of settings.
          snippets: { mode: 'matched', limit: 2, highlightTerms: true, style: 'block' },
          articleHtml: {
            base: NEWS_ROW,
            x: SOCIAL_CARD,
            linkedin: SOCIAL_CARD,
          },
          // Headings between the sources stay off — the production email runs the social cards
          // straight after the news rows — but the names are what the badge on each card says.
          sourceHeadings: {
            show: false,
            onlyWhenMixed: true,
            labels: { x: 'X', linkedin: 'in' },
            rule: false,
          },
          styles: {
            // The one thing the section bar needs: `emerald`'s is 13px, the renderer's is 12px.
            headerName: { sizePx: 13, bold: true, fontFamily: 'arial' },
          },
        },
      },
    ],
  }
}
